import { useMemo, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import {
  REGION_MIN_POINTS,
  boxShape,
  brushShape,
  encodeFog,
  fogPath,
  hideFog,
  parseFog,
  polygonShape,
  revealAllFog,
  revealFog,
  revealedFraction,
  type MapFogStored,
  type MapPoint,
  type Rings,
} from '@realmery/realm-core';
import { realms } from '../db/client.ts';

/**
 * The Atlas's fog of war (docs/PARITY-STEP-4-PLAN.md section 8), as the desktop's Fog tool:
 * fog on or off; ☀ Reveal or 🌫 Hide (X swaps them, or hold Alt for one stroke); painting
 * with the brush (its size on screen, so zooming in gives finer edges), a box, or a region's
 * outline; Undo, Reveal All and Hide All. The GM sees the fog as a see-through tint, which
 * the Legend can turn off; players see it solid, in the Player View.
 */

export type FogShape = 'brush' | 'box' | 'region';
const FOG_UNDO_LIMIT = 40;
/** The tint the GM sees through (`.atlas-fog`). */
export const FOG_TINT = 'rgb(18 20 30 / 55%)';
/** A pin's reveal: a dab this many map pixels across its point (`MapFog.brush(..., 60)`). */
export const PIN_FOG_RADIUS = 60;

export interface FogTool {
  readonly hint: string;
  readonly enabled: boolean;
  readonly revealed: Rings;
  pointerDown(event: PointerEvent<SVGSVGElement>, at: MapPoint): boolean;
  pointerMove(event: PointerEvent<SVGSVGElement>, at: MapPoint): boolean;
  pointerUp(event: PointerEvent<SVGSVGElement>, at: MapPoint): boolean;
  doubleClick(): void;
  key(event: KeyboardEvent): boolean;
  /** The GM's tint, and what's being painted, in the map's own pixels. */
  art(showTint: boolean): ReactNode;
  readonly panel: ReactNode;
  /** Reveals or covers a shape outright: a pin's or region's ☀ and 🌫 buttons. */
  apply(shape: Rings, hide: boolean): void;
}

export function useFogTool({
  file,
  mapId,
  fog,
  width,
  height,
  viewScale,
  run,
  active,
}: {
  file: string;
  mapId: string;
  fog: MapFogStored;
  width: number;
  height: number;
  viewScale: number;
  run: (work: () => Promise<unknown>, fallback: string) => Promise<boolean>;
  active: boolean;
}): FogTool {
  const [hiding, setHiding] = useState(false);
  const [shape, setShape] = useState<FogShape>('brush');
  /** The brush's width on screen, as the desktop's slider (8 to 160). */
  const [brushSize, setBrushSize] = useState(40);
  const [stroke, setStroke] = useState<{ points: readonly MapPoint[]; pointer: number; invert: boolean } | null>(null);
  const [corners, setCorners] = useState<readonly MapPoint[]>([]);
  const [hover, setHover] = useState<MapPoint | null>(null);
  const [undo, setUndo] = useState<readonly MapFogStored[]>([]);
  /** Fog of war on or off while that change is being saved. */
  const [switching, setSwitching] = useState<boolean | null>(null);
  const revealed = useMemo(() => parseFog(fog.revealed), [fog.revealed]);
  const radius = brushSize / 2 / viewScale;
  const tint = useMemo(() => (fog.enabled && width > 0 ? fogPath(true, revealed, width, height) : ''), [fog.enabled, revealed, width, height]);
  const fraction = useMemo(() => (fog.enabled && width > 0 ? revealedFraction(true, revealed, width, height) : 1), [fog.enabled, revealed, width, height]);

  /** Saves a new fog, remembering the old for Undo. */
  function commit(enabled: boolean, next: Rings, done?: () => void) {
    const before = fog;
    void run(() => realms.updateFog(file, mapId, enabled, encodeFog(next)), 'The fog could not be saved.').then((ok) => {
      if (ok) setUndo((u) => [before, ...u].slice(0, FOG_UNDO_LIMIT));
      done?.();
    });
  }

  /**
   * Painting turns fog on, as on the desktop. Hiding hides from what's showing: with fog
   * off that's the whole map, so only the painted part is covered (the desktop covers the
   * whole map there, as nothing has been revealed yet).
   */
  const paint = (area: Rings, hide: boolean) => {
    if (area.length === 0) return;
    const from = fog.enabled || !(width > 0) ? revealed : revealAllFog(width, height);
    commit(true, hide ? hideFog(from, area) : revealFog(revealed, area));
  };

  function undoOnce() {
    const [last, ...rest] = undo;
    if (!last) return;
    void run(() => realms.updateFog(file, mapId, last.enabled, last.revealed), 'That could not be undone.').then((ok) => ok && setUndo(rest));
  }

  function finishRegion(points: readonly MapPoint[]) {
    setCorners([]);
    if (points.length >= REGION_MIN_POINTS) paint(polygonShape(points), hiding);
  }

  const status = fog.enabled ? `${Math.round(fraction * 100)}% revealed to players` : 'Off - players see the whole map';

  return {
    hint:
      shape === 'brush'
        ? `Paint to ${hiding ? 'hide' : 'reveal'} · X swaps reveal and hide (or hold Alt for one stroke) · Ctrl+Z undoes`
        : shape === 'box'
          ? `Drag a box to ${hiding ? 'hide' : 'reveal'} · X swaps reveal and hide · Ctrl+Z undoes`
          : `Click corners to ${hiding ? 'hide' : 'reveal'} an outline · double-click, Enter or the first corner finishes · Esc cancels`,
    enabled: fog.enabled,
    revealed,

    pointerDown(event, at) {
      if (!active || event.button !== 0 || !event.isPrimary) return false;
      if (shape === 'region') return true;
      event.currentTarget.setPointerCapture(event.pointerId);
      setStroke({ points: [at], pointer: event.pointerId, invert: event.altKey });
      return true;
    },

    pointerMove(event, at) {
      if (!active) return false;
      setHover(at);
      if (!stroke || stroke.pointer !== event.pointerId) return shape === 'region' && corners.length > 0;
      if (shape === 'brush') {
        const last = stroke.points.at(-1)!;
        if (Math.hypot(at.x - last.x, at.y - last.y) * viewScale >= 3) setStroke({ ...stroke, points: [...stroke.points, at] });
      } else {
        setStroke({ ...stroke, points: [stroke.points[0]!, at] });
      }
      return true;
    },

    pointerUp(event, at) {
      if (!active) return false;
      if (shape === 'region') {
        const first = corners[0];
        if (first && corners.length >= REGION_MIN_POINTS && Math.hypot(first.x - at.x, first.y - at.y) * viewScale <= 10) finishRegion(corners);
        else setCorners([...corners, at]);
        return true;
      }
      const s = stroke;
      setStroke(null);
      if (!s || s.pointer !== event.pointerId) return false;
      const hide = hiding !== s.invert;
      if (shape === 'brush') paint(brushShape([...s.points, at], radius), hide);
      else if (s.points.length >= 1 && Math.hypot(at.x - s.points[0]!.x, at.y - s.points[0]!.y) * viewScale > 3) paint(boxShape(s.points[0]!, at), hide);
      return true;
    },

    doubleClick() {
      if (!active || shape !== 'region') return;
      // The double-click's own clicks each added a corner where it was: one is enough.
      finishRegion(corners.filter((p, i) => i === 0 || Math.hypot(p.x - corners[i - 1]!.x, p.y - corners[i - 1]!.y) * viewScale > 3));
    },

    key(event) {
      if (!active) return false;
      const ctrl = event.ctrlKey || event.metaKey;
      if (ctrl && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        undoOnce();
        return true;
      }
      if (ctrl || event.altKey) return false;
      if (event.key.toLowerCase() === 'x') {
        event.preventDefault();
        setHiding(!hiding);
        return true;
      }
      if (shape === 'region' && event.key === 'Enter') {
        event.preventDefault();
        finishRegion(corners);
        return true;
      }
      if (shape === 'region' && event.key === 'Backspace') {
        event.preventDefault();
        setCorners(corners.slice(0, -1));
        return true;
      }
      if (event.key === 'Escape' && (corners.length > 0 || stroke)) {
        setCorners([]);
        setStroke(null);
        return true;
      }
      return false;
    },

    art(showTint) {
      const hide = stroke ? hiding !== stroke.invert : hiding;
      const ink = hide ? 'rgb(18 20 30 / 60%)' : 'rgb(255 244 214 / 45%)';
      return (
        <g pointerEvents="none">
          {showTint && tint && <path d={tint} fill={FOG_TINT} fillRule="evenodd" className="atlas-fog" />}
          {active && stroke && shape === 'brush' && (
            <polyline points={stroke.points.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" stroke={ink} strokeWidth={radius * 2} strokeLinecap="round" strokeLinejoin="round" />
          )}
          {active && stroke && shape === 'box' && stroke.points.length === 2 && (
            <rect
              x={Math.min(stroke.points[0]!.x, stroke.points[1]!.x)}
              y={Math.min(stroke.points[0]!.y, stroke.points[1]!.y)}
              width={Math.abs(stroke.points[1]!.x - stroke.points[0]!.x)}
              height={Math.abs(stroke.points[1]!.y - stroke.points[0]!.y)}
              fill={ink}
            />
          )}
          {active && shape === 'region' && corners.length > 0 && (
            <polygon points={[...corners, ...(hover ? [hover] : [])].map((p) => `${p.x},${p.y}`).join(' ')} fill={ink} stroke="#ffffff" strokeWidth={2} strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
          )}
          {active && shape === 'brush' && hover && !stroke && <circle cx={hover.x} cy={hover.y} r={radius} fill="none" stroke="#ffffff" strokeWidth={1.5} vectorEffect="non-scaling-stroke" className="atlas-fog-brush" />}
        </g>
      );
    },

    apply(area, hide) {
      paint(area, hide);
    },

    panel: (
      <div className="atlas-draw-panel atlas-fog-panel" role="group" aria-label="Fog">
        <h2>Fog of war</h2>
        <label className="check" title="Turning fog off shows players the whole map; turning it back on restores what you'd revealed">
          <input
            type="checkbox"
            checked={switching ?? fog.enabled}
            onChange={(e) => {
              // Shown at once; the saved value takes over when it's back.
              setSwitching(e.currentTarget.checked);
              commit(e.currentTarget.checked, revealed, () => setSwitching(null));
            }}
          />{' '}
          Fog of war on
        </label>
        <div className="view-switch" role="group" aria-label="Reveal or hide">
          <button type="button" className={!hiding ? 'tab selected' : 'tab'} aria-pressed={!hiding} title="Clear the fog where you paint (X swaps)" onClick={() => setHiding(false)}>
            ☀ Reveal
          </button>
          <button type="button" className={hiding ? 'tab selected' : 'tab'} aria-pressed={hiding} title="Put the fog back where you paint (X swaps)" onClick={() => setHiding(true)}>
            🌫 Hide
          </button>
        </div>
        <div className="view-switch" role="group" aria-label="Paint with">
          {(
            [
              ['brush', '🖌 Brush'],
              ['box', '▭ Box'],
              ['region', '⬠ Region'],
            ] as const
          ).map(([s, label]) => (
            <button
              key={s}
              type="button"
              className={shape === s ? 'tab selected' : 'tab'}
              aria-pressed={shape === s}
              onClick={() => {
                setShape(s);
                setCorners([]);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {shape === 'brush' && (
          <label title="Brush size (on screen - zoom in for finer edges)">
            Brush size
            <input type="range" min={8} max={160} step={1} value={brushSize} onChange={(e) => setBrushSize(Number(e.currentTarget.value))} />
          </label>
        )}
        <div className="actions">
          <button type="button" disabled={undo.length === 0} title="Undo the last fog change (Ctrl+Z)" onClick={undoOnce}>
            ↶ Undo
          </button>
          <button type="button" disabled={width === 0} title="Clear the fog from the whole map (Undo brings it back)" onClick={() => commit(true, revealAllFog(width, height))}>
            Reveal All
          </button>
          <button type="button" title="Cover the whole map again (Undo brings it back)" onClick={() => commit(true, [])}>
            Hide All
          </button>
        </div>
        <p className="meta" role="status">
          {status}
        </p>
      </div>
    ),
  };
}
